/**
 * Turning an API failure into a sentence the operator can act on (SPEC.md §9.1).
 *
 * The backend sends an error *code* and interpolation params, never a translated sentence, so the
 * wording lives here with the rest of the UI text.
 */
import { ApiError, PASSWORD_CHANGE_REQUIRED } from '@/shared/api/http'
import { formatMoney, formatQuantity, parseDecimalInput } from '@/shared/lib/decimal'

/**
 * How to turn a key into a sentence.
 *
 * Injected rather than imported: §10.1 puts `shared` at the bottom, and the i18n instance is
 * created in `app/`. The same shape `http.ts` already uses for its session-expiry callback, and for
 * the same reason — the layer below must not reach up to find the layer above.
 *
 * Until `app/i18n` registers one, a key is returned unchanged. That is the right failure: an
 * untranslated key on screen is ugly and obvious, where a thrown error here would replace a real
 * message about the operator's document with a message about our wiring.
 */
type Translator = (key: string, params?: Record<string, unknown>) => string

let translate: Translator = (key) => key

/** Called once at start-up by `app/i18n`. */
export function setTranslator(next: Translator): void {
  translate = next
}

/**
 * Params ready for interpolation.
 *
 * A list param — the document numbers blocking a cancellation, say — reaches vue-i18n as an array
 * and renders as `CHQ-1,CHQ-2` or worse, so it is joined here once rather than in every message.
 * An empty list becomes an empty string, which is what lets a message drop the clause entirely.
 */
function renderable(code: string, params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).map(([name, value]) => [
      name,
      Array.isArray(value) ? value.join(', ') : formatParam(code, name, value),
    ]),
  )
}

/**
 * Money the server names in a refusal: a party's open balance (`PARTY_HAS_BALANCE`) and the payroll
 * figures (`ADVANCE_EXCEEDS_EARNINGS`, `DEDUCTION_EXCEEDS_DEBT`, `DEDUCTION_EXCEEDS_PAY`).
 */
const MONEY_PARAMS = new Set(['balance', 'advance', 'earned', 'deduction', 'owed'])

/**
 * A name that is money in one refusal and a quantity in another. `remaining` is the pay left after
 * advances in `DEDUCTION_EXCEEDS_PAY`, but what a lot still holds in `BATCH_OVER_RECEIVED`.
 */
const MONEY_PARAMS_BY_CODE: Record<string, ReadonlySet<string>> = {
  DEDUCTION_EXCEEDS_PAY: new Set(['remaining']),
}

/** Quantities in base units, from the stock refusals (`INSUFFICIENT_BATCH`, `BATCH_OVER_…`). */
const QUANTITY_PARAMS = new Set([
  'available',
  'requested',
  'received',
  'remaining',
  'delivered',
  'already_returned',
])

/**
 * A decimal param printed the way the rest of the screen prints it.
 *
 * The server sends `NUMERIC` values as strings — `120000.00`, `10.000` — and interpolating them
 * as-is put the very figures audit U-02 removed from the templates back into the error sentences,
 * beside a page that reads `120 000,00` and `10`. A value that is not a number is left alone.
 */
function formatParam(code: string, name: string, value: unknown): unknown {
  if (typeof value !== 'string' || parseDecimalInput(value) === null) return value
  if (MONEY_PARAMS.has(name) || MONEY_PARAMS_BY_CODE[code]?.has(name)) return formatMoney(value)
  if (QUANTITY_PARAMS.has(name)) return formatQuantity(value)
  return value
}

/**
 * Codes the application answers on its own, by navigating, so no screen should word them too.
 * A temporary password sends the user to the change-password screen; a toast saying so on top of
 * the screen they were taken from would be noise about a page they have already left.
 */
const ANSWERED_BY_NAVIGATION = new Set([PASSWORD_CHANGE_REQUIRED])

/**
 * Whether a failure is handled globally (a redirect) and should not be shown where it happened.
 *
 * @example
 * isAnsweredByNavigation(new ApiError(403, 'PASSWORD_CHANGE_REQUIRED', 'x')) // true
 */
export function isAnsweredByNavigation(error: unknown): boolean {
  return error instanceof ApiError && ANSWERED_BY_NAVIGATION.has(error.code)
}

/**
 * Human-readable message for any thrown value — empty for a failure answered by navigation, which
 * is what keeps `toast.failure(describeError(error))` silent for it at every call site.
 *
 * @example
 * catch (error) { message.value = describeError(error) }
 */
export function describeError(error: unknown): string {
  if (isAnsweredByNavigation(error)) return ''
  if (error instanceof ApiError) {
    const key = error.messageKey
    const translated = translate(key, renderable(error.code, error.params))
    // vue-i18n echoes the key back when it is missing; falling through to a generic sentence is
    // better than showing `errors.SOMETHING_NEW` to a warehouse keeper.
    return translated === key ? translate('errors.UNKNOWN_ERROR') : translated
  }
  return translate('errors.UNKNOWN_ERROR')
}

/**
 * Password-policy problems, translated (the backend returns keys like `password.too_short`).
 *
 * @example
 * describePasswordProblems(error) // ['Kamida 8 ta belgi bo'lishi kerak']
 */
export function describePasswordProblems(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.code !== 'WEAK_PASSWORD') {
    return []
  }
  const problems = error.params.problems
  return Array.isArray(problems) ? problems.map((problem) => translate(String(problem))) : []
}
